import { ComponentRef, Directive, inject, input, ViewContainerRef } from '@angular/core'
import { toObservable } from '@angular/core/rxjs-interop'
import { FormControl, FormGroupDirective, NgForm, ValidationErrors } from '@angular/forms'
import pascalCase from 'just-pascal-case'
import { distinctUntilChanged, EMPTY, map, merge, Observable, share, startWith, switchMap, withLatestFrom } from 'rxjs'

import { InputFieldErrorMessageComponent } from './input-field-error-message.component'

@Directive({ selector: 'ng-template[paiInputFieldError]', standalone: true })
export class InputFieldErrorDirective {
  readonly control = input.required<FormControl<any>>({ alias: 'paiInputFieldError' })

  readonly #container = inject(ViewContainerRef)
  readonly #form: FormGroupDirective | NgForm | null =
    inject(FormGroupDirective, { optional: true }) || inject(NgForm, { optional: true })

  #ref: ComponentRef<InputFieldErrorMessageComponent> | null = null

  constructor() {
    const control$ = toObservable(this.control).pipe(share())

    const statusChanges$ = control$.pipe(
      switchMap((c) =>
        c.events.pipe(
          startWith(void 0),
          map(() => this.getErrors(false, c)),
        ),
      ),
    )

    const onSubmit$ = (<Observable<any>>this.#form?.ngSubmit ?? EMPTY).pipe(
      withLatestFrom(control$),
      map(([, c]) => this.getErrors(true, c)),
    )
    merge(statusChanges$, onSubmit$).pipe(distinctUntilChanged()).subscribe(this.update)
  }

  private readonly update = (errors: ValidationErrors | null) => {
    if (errors) {
      const [errorName, errorValue] = Object.entries(errors)[0]
      this.#ref = this.#ref ?? this.#container.createComponent(InputFieldErrorMessageComponent)
      this.#ref.setInput('key' satisfies keyof InputFieldErrorMessageComponent, `Form.Error.${pascalCase(errorName)}`)
      this.#ref.setInput('value' satisfies keyof InputFieldErrorMessageComponent, errorValue ?? null)
    } else {
      this.#container.clear()
      this.#ref = null
    }
  }

  private getErrors(force: boolean, control: FormControl): ValidationErrors | null {
    return control.errors && (control.touched || force) ? control.errors : null
  }
}
